import { cuotasDisponiblesDesdeLibro } from "./cobro/resolverSaldo";
import { conDocumentoId } from "./documentosFiscales";
import { client } from "./supabase";

// ─── PLAN COBROS (facturas emitidas) ─────────────────────

export const getPlanCobros = async (facturaSiiEmitidaId) => {
  const { data, error } = await client
    .from("plan_cobros_facturas")
    .select("*")
    .eq("factura_sii_emitida_id", facturaSiiEmitidaId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

export const crearPlanCobros = async (cuotas) => {
  // Dual-write Fase 3 (plano supertipo documentos_fiscales): además de
  // factura_sii_emitida_id, se escribe documento_id resolviendo contra
  // documentos_fiscales (tipo='sii_emitida'). No reemplaza la columna typed.
  const filas = await conDocumentoId(cuotas, "sii_emitida", "factura_sii_emitida_id");
  const { data, error } = await client.from("plan_cobros_facturas").insert(filas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaCobro = async (id, cambios) => {
  const { data, error } = await client.from("plan_cobros_facturas").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

export const eliminarPlanCobros = async (facturaSiiEmitidaId) => {
  const { error } = await client
    .from("plan_cobros_facturas")
    .delete()
    .eq("factura_sii_emitida_id", facturaSiiEmitidaId);
  if (error) throw error;
};

// ─── PLAN PAGOS (facturas recibidas) ─────────────────────

export const getPlanPagos = async (facturaSiiRecibidaId) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .select("*")
    .eq("factura_sii_recibida_id", facturaSiiRecibidaId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

// Fase 5a — carga de cuotas por documento_id (supertipo documentos_fiscales). Lo usa el
// cronograma de EXTERNAS, cuya identidad NO vive en factura_sii_recibida_id (es NULL) sino
// en documento_id (tipo='factura_externa'). Para SII recibidas se sigue usando getPlanPagos.
export const getPlanPagosPorDocumento = async (documentoId) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .select("*")
    .eq("documento_id", documentoId)
    .order("numero_cuota");
  if (error) throw error;
  return data || [];
};

export const crearPlanPagos = async (cuotas) => {
  // Dual-write Fase 3 (plano supertipo documentos_fiscales): además de
  // factura_sii_recibida_id, se escribe documento_id resolviendo contra
  // documentos_fiscales (tipo='sii_recibida'). No reemplaza la columna typed.
  const filas = await conDocumentoId(cuotas, "sii_recibida", "factura_sii_recibida_id");
  const { data, error } = await client.from("plan_pagos_facturas").insert(filas).select();
  if (error) throw error;
  return data || [];
};

export const actualizarCuotaPago = async (id, cambios) => {
  const { data, error } = await client.from("plan_pagos_facturas").update(cambios).eq("id", id).select().single();
  if (error) throw error;
  return data;
};

export const eliminarPlanPagos = async (facturaSiiRecibidaId) => {
  const { error } = await client
    .from("plan_pagos_facturas")
    .delete()
    .eq("factura_sii_recibida_id", facturaSiiRecibidaId);
  if (error) throw error;
};

// Fase 5a — borrado del plan por documento_id (externas). Espejo de eliminarPlanPagos.
export const eliminarPlanPagosPorDocumento = async (documentoId) => {
  const { error } = await client.from("plan_pagos_facturas").delete().eq("documento_id", documentoId);
  if (error) throw error;
};

// Borra UNA cuota de pago por id, SOLO si está pendiente (el `.eq("estado","pendiente")` es el
// candado en DB). Nunca toca cuotas pagadas — están vinculadas a un movimiento histórico vía FK
// movimientos_bancarios.plan_pago_cuota_id. Si la cuota está pagada (o no existe), no borra nada
// y lanza error.
export const eliminarCuotaPago = async (id) => {
  const { data, error } = await client
    .from("plan_pagos_facturas")
    .delete()
    .eq("id", id)
    .eq("estado", "pendiente")
    .select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`No se pudo borrar la cuota ${id} (¿ya está pagada?)`);
  return data[0];
};

// ─── Obtener todas las cuotas futuras pendientes ──────────

// `facturaId` opcional: acota la carga a las cuotas de UNA factura emitida (refetch dirigido tras
// editar su plan de cobro). Sin el filtro, comportamiento idéntico al de siempre.
export const getCuotasCobrosFuturas = async (pais, { facturaId } = {}) => {
  // F3: la disponibilidad de cada cuota se DERIVA del libro `aplicaciones_saldo`
  // (`cuotasDisponiblesDesdeLibro`), NO del flag `estado` escalar. Se carga el plan COMPLETO
  // (cualquier estado) y se filtra por lo que queda pendiente según Σ eventos por `cuota_id` — así
  // una cuota parcialmente pagada aparece con su saldo REAL (monto_cuota = remanente), y una cuota
  // saldada por el libro queda fuera aunque su escalar no se haya actualizado. Único consumidor:
  // FlujoCajaV2 (rama de cuotas de "Por cobrar"). Paridad con el escalar verificada por dual-run F3.
  let query = client
    .from("plan_cobros_facturas")
    .select(
      `
      *,
      factura:facturas_sii_emitidas(
        folio, razon_social_receptor, pais
      )
    `
    )
    .eq("factura.pais", pais);
  if (facturaId != null) query = query.eq("factura_sii_emitida_id", facturaId);
  const { data, error } = await query.order("fecha_vencimiento");
  if (error) throw error;
  const todas = data || [];
  if (todas.length === 0) return todas;

  // Eventos del libro para estas cuotas (via `cuota_id`, eje cobro → documento_tipo='sii_emitida').
  // El filtro de documento_tipo es OBLIGATORIO: los id de plan_cobros_facturas y plan_pagos_facturas
  // pueden colisionar numéricamente (secuencias distintas), y un evento de pago con el mismo cuota_id
  // no debe restar acá.
  const cuotaIds = todas.map((c) => c.id);
  const { data: eventos, error: errEv } = await client
    .from("aplicaciones_saldo")
    .select("cuota_id, monto_documento")
    .eq("documento_tipo", "sii_emitida")
    .in("cuota_id", cuotaIds);
  if (errEv) throw errEv;

  // `total_cuotas` = total del PLAN (todas las cuotas del set completo, por factura), para el label
  // "cuota N/M". Se deriva del set ya cargado (sin query extra: ya no filtramos por estado).
  const totalPorFactura = {};
  for (const c of todas) {
    if (c.factura_sii_emitida_id != null)
      totalPorFactura[c.factura_sii_emitida_id] = (totalPorFactura[c.factura_sii_emitida_id] || 0) + 1;
  }

  // Disponibles según el libro (monto_cuota reemplazado por el remanente); preserva el orden por
  // fecha_vencimiento del query.
  return cuotasDisponiblesDesdeLibro(todas, eventos || []).map((c) => ({
    ...c,
    total_cuotas: totalPorFactura[c.factura_sii_emitida_id] || 0,
  }));
};

// Cuotas de pago pendientes para "Por pagar". Trae TRES tipos de documento:
//   · SII recibidas — por factura_sii_recibida_id (embed a facturas_sii_recibidas, filtro por país).
//   · Externas (Fase 5a-bis) y boletas de honorarios (Fase 5b) — por documento_id (tipos
//     'factura_externa' / 'boleta_honorario'); su factura_sii_recibida_id es NULL, así que NO cuelgan
//     del embed a SII. Se marca `factura_externa_id` / `factura_boleta_id` (= documentos_fiscales.
//     origen_id) para que FlujoCajaV2 matchee por id del documento base.
// `facturaId` + `esExterna`/`esBoleta` opcionales: refetch DIRIGIDO a UNA factura (tras editar su
// plan). Sin ellos, comportamiento completo (los tres tipos). El total_cuotas se cuenta por
// documento_id, criterio único válido para todos (cuelgan de documento_id desde Fase 2/3).
export const getCuotasPagosFuturas = async (pais, { facturaId = null, esExterna = false, esBoleta = false } = {}) => {
  const cuotas = [];
  const dirigidoDoc = facturaId != null && (esExterna || esBoleta);
  const dirigidoRec = facturaId != null && !esExterna && !esBoleta;

  // ── SII recibidas. Se omite si el refetch es dirigido a externa/boleta. ──
  if (!dirigidoDoc) {
    let qRec = client
      .from("plan_pagos_facturas")
      .select("*, factura:facturas_sii_recibidas(folio, razon_social_emisor, pais)")
      .eq("factura.pais", pais);
    if (dirigidoRec) qRec = qRec.eq("factura_sii_recibida_id", facturaId);
    const { data, error } = await qRec.order("fecha_vencimiento");
    if (error) throw error;
    cuotas.push(...(data || []));
  }

  // ── Externas + boletas: por documento_id, sin embed a SII. Se omite si el refetch es dirigido a SII. ──
  if (!dirigidoRec) {
    let qDoc = client.from("plan_pagos_facturas").select("*, documento:documentos_fiscales!inner(tipo, origen_id)");
    if (facturaId != null && esExterna) {
      qDoc = qDoc.eq("documento.tipo", "factura_externa").eq("documento.origen_id", facturaId);
    } else if (facturaId != null && esBoleta) {
      qDoc = qDoc.eq("documento.tipo", "boleta_honorario").eq("documento.origen_id", facturaId);
    } else {
      qDoc = qDoc.in("documento.tipo", ["factura_externa", "boleta_honorario"]);
    }
    const { data, error } = await qDoc.order("fecha_vencimiento");
    if (error) throw error;
    cuotas.push(
      ...(data || []).map((c) => ({
        ...c,
        factura_externa_id: c.documento?.tipo === "factura_externa" ? c.documento.origen_id : null,
        factura_boleta_id: c.documento?.tipo === "boleta_honorario" ? c.documento.origen_id : null,
      }))
    );
  }

  if (cuotas.length === 0) return cuotas;

  // F3: disponibilidad por el libro `aplicaciones_saldo` (Σ eventos por `cuota_id`), NO por el flag
  // `estado` escalar. Se cargó el plan COMPLETO arriba (sin filtro de estado); acá se queda con las
  // cuotas que aún tienen saldo remanente > tolerancia, con monto_cuota = remanente. Cubre los 3
  // tipos de documento (sii_recibida / factura_externa / boleta_honorario); el filtro de
  // documento_tipo evita que un evento de COBRO con el mismo cuota_id (secuencias que pueden colisionar)
  // reste acá. Paridad con el escalar verificada por dual-run F3. `cuotasDisponiblesDesdeLibro` preserva
  // los campos mapeados (factura_externa_id / factura_boleta_id) por el spread.
  const cuotaIds = cuotas.map((c) => c.id);
  const { data: eventos, error: errEv } = await client
    .from("aplicaciones_saldo")
    .select("cuota_id, monto_documento")
    .in("documento_tipo", ["sii_recibida", "factura_externa", "boleta_honorario"])
    .in("cuota_id", cuotaIds);
  if (errEv) throw errEv;
  const disponibles = cuotasDisponiblesDesdeLibro(cuotas, eventos || []);
  if (disponibles.length === 0) return disponibles;

  // Enriquecer con `total_cuotas` = total del PLAN (todas las cuotas, cualquier estado), por
  // documento_id — sirve para recibidas, externas Y boletas. El fallback del consumidor cubre un
  // documento_id nulo hipotético (no debería ocurrir: backfill Fase 2 + dual-write Fase 3).
  const documentoIds = [...new Set(disponibles.map((c) => c.documento_id).filter((id) => id != null))];
  const { data: todas, error: errTotal } = await client
    .from("plan_pagos_facturas")
    .select("documento_id")
    .in("documento_id", documentoIds);
  if (errTotal) throw errTotal;
  const totalPorDoc = {};
  for (const row of todas || []) totalPorDoc[row.documento_id] = (totalPorDoc[row.documento_id] || 0) + 1;
  return disponibles.map((c) => ({ ...c, total_cuotas: totalPorDoc[c.documento_id] || 0 }));
};
